import { Box, ScaleFactorProvider } from "arkh";
import Animated, {
  useAnimatedRef,
  useAnimatedScrollHandler,
  useSharedValue,
} from "react-native-reanimated";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { StorecastState } from "./types";
import {
  AllWidgets,
  useWidgetsToPos,
  WidgetsEmployeeIdProvider,
} from "widgets";
import { useDeleteWidget } from "./useDeleteWidget";
import { useRearangeWidgets } from "./useRearangeWidgets";

import { EmptyPositionT } from "./EmptyPositions/EmptyPosType";
import { EmptyPositions } from "./EmptyPositions/EmptyPositions";
import { FocusFrame } from "./Focus/FocusFrame";
import {
  InteractiveWidgetWrapper,
  InteractiveWidgetWrapperProps,
} from "./Widget/InteractiveWidgetWrapper";

import {
  WidgetNoteEdit,
  WidgetNoteEditQuery,
  WIDGET_NOTE_EDIT_QUERY,
} from "./WidetNoteEdit/WidgetNoteEdit";

import { useNavigation } from "@react-navigation/core";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useWidgetEventHandler } from "./useWidgetEventHandler";

import { UserLoggedInNavigatorNavigationProp } from "../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";

import {
  graphql,
  useFragment,
  useLazyLoadQuery,
  useQueryLoader,
  useSubscription,
} from "react-relay";
import { AllWidgets_widget$key } from "widgets/src/__generated__/AllWidgets_widget.graphql";
import { WidgetEvent } from "widgets/src/WidgetEventT";
import { StorecastFocusQuery } from "./__generated__/StorecastFocusQuery.graphql";
import { useOnBeforeLeaveScreen } from "./useOnBeforeLeaveScreen";
import { ErrorBoundary } from "react-error-boundary";

import { useEvent } from "../../../common/useEvent";
import { WidgetSizeT } from "./localTypes/WidgetSizeT";
import { useOnStorecastLeaveScreen } from "./useOnStorecastLeaveScreen";
import { captureException } from "../../../common/sentry";

import { WidgetErrorFallback } from "./WidgetErrorFallback";
import { StorecastEmployeeRoleDialog } from "./StorecastEmployeeRoleDialog";

import { useWidgetMargin } from "./Widget/useWidgetMargin";
import { registerScreen } from "../../../common/analytics";
import { supportedWidgetTypes } from "./supportedWidgetTypes";
import { Storecast_Dashboard$key } from "./__generated__/Storecast_Dashboard.graphql";
import { StorecastRearrangeSubscription } from "./__generated__/StorecastRearrangeSubscription.graphql";
import { Portal } from "@gorhom/portal";
import { AbsolutePopoverHostName } from "../../../components/mobile/AbsolutePopover";
import { View } from "react-native";
import { StorecastChangedSubscription } from "./__generated__/StorecastChangedSubscription.graphql";
import { Storecast_Store$key } from "./__generated__/Storecast_Store.graphql";
import { notEmpty } from "@/src/utils";
import { usePersistedEmployeeId } from "@/src/OS/Login/usePersistedEmployeeId";

export function Storecast({
  unitSize,
  fetchKey,
  storecastState,
  _setStorecastState,
  dashboard: _dashboard,
  store: _store,
  headerHeight,
  pollFetchKey,
}: {
  storecastState: StorecastState;
  _setStorecastState: (state: StorecastState) => void;
  fetchKey: string;
  dashboard: Storecast_Dashboard$key;
  store: Storecast_Store$key;
  unitSize: number;
  headerHeight: number;
  pollFetchKey: string;
}) {
  const store = useFragment(
    graphql`
      fragment Storecast_Store on Store {
        myRole
      }
    `,
    _store
  );
  const dashboard = useFragment(
    graphql`
      fragment Storecast_Dashboard on Dashboard {
        id
        defaultTeams {
          id
        }
        canEdit
        widgets {
          id
          __typename
          ...useWidgetToPos_widget
          ...AllWidgets_widget
          ...InteractiveWidgetWrapper_widget
          ... on WidgetNote {
            note {
              ...NoteEditSharedInfo_widgetNote
              ...NoteEdit
            }
          }
        }
      }
    `,
    _dashboard
  );

  useSubscription<StorecastChangedSubscription>(
    useMemo(
      () => ({
        variables: { storecastId: dashboard.id },
        subscription: graphql`
          subscription StorecastChangedSubscription($storecastId: ID!) {
            storecastChanged(storecastId: $storecastId) {
              id
              name
              widgets {
                id
                __typename
                ...useWidgetToPos_widget
                ...AllWidgets_widget
                ...InteractiveWidgetWrapper_widget
                ... on WidgetNote {
                  note {
                    ...NoteEditSharedInfo_widgetNote
                    ...NoteEdit
                  }
                }
              }
            }
          }
        `,
      }),
      [dashboard.id]
    )
  );

  useSubscription<StorecastRearrangeSubscription>(
    useMemo(
      () => ({
        variables: {
          storecastId: dashboard.id,
        },
        subscription: graphql`
          subscription StorecastRearrangeSubscription($storecastId: ID!) {
            widgetsRearranged(storecastId: $storecastId) {
              widgets {
                id
                ...useWidgetToPos_widget
              }
            }
          }
        `,
      }),
      [dashboard.id]
    )
  );

  const widgets = useMemo(
    () => (dashboard.widgets ?? []).filter((a) => !!a),
    [dashboard.widgets]
  );

  const supportedWidgets = useMemo(
    () =>
      widgets
        .filter(notEmpty)
        .filter((w) => supportedWidgetTypes.includes(w!.__typename))
        .sort((a, b) => a.id.localeCompare(b.id)),
    [widgets]
  );

  const storecastContainsUnsupportedWidgets = useMemo(
    () => supportedWidgets.length !== widgets.length,
    [supportedWidgets.length, widgets.length]
  );

  const _remotePositions = useWidgetsToPos(supportedWidgets);

  const remotePositions = useMemo(() => _remotePositions, [_remotePositions]);

  useEffect(() => {
    if (
      supportedWidgets.length === 0 &&
      (store.myRole === "MANAGER" || store.myRole === "ADMIN")
    ) {
      _setStorecastState("EDIT");
    }
  }, [_setStorecastState, supportedWidgets.length, store.myRole]);
  const setStorecastState = useCallback(
    (inpState: StorecastState) => {
      if (storecastContainsUnsupportedWidgets && inpState === "EDIT") {
        return;
      }
      _setStorecastState(inpState);
    },
    [_setStorecastState, storecastContainsUnsupportedWidgets]
  );

  const navigation =
    useNavigation<UserLoggedInNavigatorNavigationProp<"Dashboard">>();

  const positions = useSharedValue(remotePositions);
  useMemo(() => {
    positions.value = [...remotePositions];
  }, [positions, remotePositions]);

  const openWidgetSettings = useCallback(
    ({
      widgetId,
      __typename,
      size,
    }: {
      widgetId: string;
      __typename: string;
      size: WidgetSizeT;
    }) => {
      const params = {
        unitSize,
        size,
        widgetId,
        storecastId: dashboard.id,
      };

      switch (__typename) {
        case "WidgetFile":
          navigation.navigateDeprecated("WSFile", {
            widgetId,
            unitSize,

            isOrg: false,
          });
          break;
        case "WidgetTodoSummary":
          navigation.navigateDeprecated("WSTodoSummary", params);
          break;
        case "WidgetCalendarOverview":
          navigation.navigate("WSCalendarOverview", params);
          break;
        case "WidgetTodoOverview":
          navigation.navigateDeprecated("WSTodoOverview", params);
          break;
        case "WidgetRoutineOverview":
          navigation.navigateDeprecated("WSChecklistOverview", params);
          break;
        case "WidgetSignalOverview":
          navigation.navigateDeprecated("WSSignalOverview", params);
          break;
        case "WidgetTaskCollection":
          navigation.navigateDeprecated("WSTaskCollection", params);
          break;
        case "WidgetTaskSingle":
          navigation.navigateDeprecated("WSTaskSingle", params);
          break;
        case "WidgetEmployeeSchedule":
          navigation.navigateDeprecated("WSEmployeeSchedule", params);
          break;
        case "WidgetEmployeeLeave":
          navigation.navigate("WSEmployeeLeave", params);
          break;
        case "WidgetEmployeeRoleOverview":
          navigation.navigate("WSEmployeeRoleOverview", params);
          break;
        case "WidgetNote":
          navigation.navigateDeprecated("WSNote", {
            widgetId,
          });
          break;
      }
    },
    [navigation, dashboard.id, unitSize]
  );

  const openWidgetAdd = useCallback(
    (position: EmptyPositionT) => {
      navigation.navigateDeprecated("WidgetAdd", {
        position,
        storecastId: dashboard.id,
        defaultTeams: dashboard.defaultTeams?.map(({ id }) => id) ?? [],
      });
    },
    [dashboard.defaultTeams, navigation, dashboard.id]
  );

  const setWidgets = useRearangeWidgets(dashboard.id);
  const deleteWidget = useDeleteWidget(dashboard.id);

  const scrollRef = useAnimatedRef<Animated.ScrollView>();
  const scrollX = useSharedValue(0);
  const scrollHandler = useAnimatedScrollHandler(
    (e) => (scrollX.value = e.contentOffset.x)
  );

  const hiddenWidgetId = useSharedValue<string | null>(null);
  const nextHiddenWidgetIdValue = useRef(false);
  if (nextHiddenWidgetIdValue.current) {
    // hmm not safe?
    // Component not side effect free
    hiddenWidgetId.value = null;
    nextHiddenWidgetIdValue.current = false;
  }

  const [widgetNoteEditQueryRef, loadNoteEditQuery, disposeNoteEditQuery] =
    useQueryLoader<WidgetNoteEditQuery>(WIDGET_NOTE_EDIT_QUERY);

  const [pageId, setPageId] = useState<string | null>(null);

  const onNoteEdit = useCallback(
    (widgetId: string, pageId: string) => {
      if (widgetNoteEditQueryRef) {
        return;
      }
      setPageId(pageId);

      loadNoteEditQuery({ id: widgetId }, { fetchPolicy: "store-and-network" });
      registerScreen(
        "WidgetNoteEdit",
        {
          widgetId,
          pageId,
        },
        true
      );
    },
    [loadNoteEditQuery, widgetNoteEditQueryRef]
  );

  const navigateDeprecatedToKPI = useCallback(
    (
      routineId: string,
      routineTitle: string,
      sequenceNumber: number,
      sectionIds?: string[]
    ) => {
      navigation.navigateDeprecated("KPI", {
        routineId,
        routineTitle,
        sequenceNumber,
        sectionIds,
      });
    },
    [navigation]
  );

  const [roleDialogData, setRoleDialogData] = useState<{
    roleId: string;
    roleName: string;
    teamId: string;
    teamName: string;
    teamColor: string | null;
    employeeIds: string[];
  } | null>(null);

  const onEmployeeRoleSelect = (data: {
    roleId: string;
    roleName: string;
    teamId: string;
    teamName: string;
    teamColor: string | null;
    employeeIds: string[];
  }) => {
    setRoleDialogData(data);
  };

  const [onWidgetEvent] = useWidgetEventHandler({
    openKPI: navigateDeprecatedToKPI,
    onNoteEdit,
    onEmployeeRoleSelect,
  });
  const onStorecastLeaveScreen = useOnStorecastLeaveScreen(dashboard.id);
  useOnBeforeLeaveScreen(() => {
    if (storecastState === "FOCUS") {
      onStorecastLeaveScreen();
      setStorecastState("IDLE");
    }
  });

  const onLongPress = useEvent(() => {
    if (storecastState !== "FOCUS") {
      setStorecastState("EDIT");
    }
  });
  const viewRef = useRef<View | null>(null);

  const margin = useWidgetMargin();
  const insetsBottom = useSafeAreaInsets().bottom;
  const columnHorizontalPadding = 0;
  return (
    <>
      <Box
        ref={viewRef}
        style={{
          flex: 1,
          overflow: "hidden",
        }}
        color="surface-background"
      >
        <ScaleFactorProvider value={unitSize / 236}>
          <Animated.ScrollView
            ref={scrollRef}
            onScroll={scrollHandler}
            scrollEventThrottle={16}
            horizontal
            disableIntervalMomentum
            nestedScrollEnabled
            keyboardDismissMode="on-drag"
            keyboardShouldPersistTaps="always"
            showsVerticalScrollIndicator={false}
            alwaysBounceHorizontal={false}
            overScrollMode="never"
            alwaysBounceVertical={false}
            showsHorizontalScrollIndicator
            scrollEnabled={widgetNoteEditQueryRef === null}
            style={{
              width: "100%",
            }}
            contentContainerStyle={{
              width: unitSize * 8 + columnHorizontalPadding * 4,
              height: unitSize * 4,
              paddingBottom: insetsBottom,
              flexDirection: "column",
            }}
          >
            {storecastState === "FOCUS" ? (
              <FocusFrame
                spaceBetweenColumns={columnHorizontalPadding}
                positions={positions}
                unitSize={unitSize}
                storecastId={dashboard.id}
              />
            ) : null}
            {storecastState === "EDIT" ? (
              <EmptyPositions
                columnHorizontalPadding={columnHorizontalPadding}
                positions={positions}
                unitSize={unitSize}
                onRequestAdd={openWidgetAdd}
              />
            ) : null}

            {supportedWidgets.map((w) => {
              return (
                <Widget
                  id={w.id}
                  key={w.id}
                  hiddenWidgetId={hiddenWidgetId}
                  renderContext="storecast"
                  setWidgets={setWidgets}
                  onRequestEdit={onLongPress}
                  isEditing={storecastState === "EDIT"}
                  widgets={positions as any}
                  unitSize={unitSize}
                  scrollX={scrollX}
                  scrollRef={scrollRef}
                  spaceBetweenColumns={columnHorizontalPadding}
                  openSettings={openWidgetSettings}
                  requestDeleteWidget={deleteWidget}
                  widget={w}
                  canEdit={dashboard.canEdit}
                  storecastState={storecastState === "FOCUS" ? "FOCUS" : "IDLE"}
                  isSpace={w.__typename === "WidgetSpace"}
                  margin={margin}
                  fetchKey={fetchKey}
                  storecastId={dashboard.id}
                  onEvent={onWidgetEvent}
                  selectedIssueId={undefined}
                  pollFetchKey={pollFetchKey}
                />
              );
            })}
          </Animated.ScrollView>
          {roleDialogData ? (
            <StorecastEmployeeRoleDialog
              roleId={roleDialogData.roleId}
              roleName={roleDialogData.roleName}
              teamId={roleDialogData.teamId}
              teamName={roleDialogData.teamName}
              teamColor={roleDialogData.teamColor}
              employeeIds={roleDialogData.employeeIds}
              onClose={() => setRoleDialogData(null)}
            />
          ) : null}
          {widgetNoteEditQueryRef && pageId ? (
            <Portal hostName={AbsolutePopoverHostName}>
              <WidgetNoteEdit
                headerHeight={headerHeight}
                queryRef={widgetNoteEditQueryRef}
                pageId={pageId}
                columnHorizontalPadding={columnHorizontalPadding}
                onClosed={() => {
                  nextHiddenWidgetIdValue.current = true;
                  disposeNoteEditQuery();
                  setPageId(null);
                }}
                onStart={() => {
                  hiddenWidgetId.value = widgetNoteEditQueryRef.variables.id;
                }}
                scrollX={scrollX}
                unitSize={unitSize}
              />
            </Portal>
          ) : null}
        </ScaleFactorProvider>
      </Box>
    </>
  );
}

const Widget = React.memo(InnerWidget);

function InnerWidget(
  props: {
    id: string;
    storecastState: StorecastState;
    widget: AllWidgets_widget$key;
    fetchKey: string;
    storecastId: string;
    onEvent: (event: WidgetEvent) => void;
    selectedIssueId?: string;
    isSpace?: boolean;
    margin: number;
    pollFetchKey: string;
  } & InteractiveWidgetWrapperProps
) {
  const [errorKey, setErrorKey] = React.useState(0);
  const focus = useLazyLoadQuery<StorecastFocusQuery>(
    graphql`
      query StorecastFocusQuery($id: ID!) {
        dashboard(id: $id) {
          focusedNodeId
          focusedDetailItemId
        }
      }
    `,
    {
      id: props.storecastId,
    }
  );

  const isFocused = focus.dashboard?.focusedNodeId === props.id;
  const onReset = useCallback(() => setErrorKey((k) => k + 1), []);
  const onError = useCallback(
    (e: Error, info: { componentStack: string }): void => {
      captureException(e, { extra: { info } });
    },
    []
  );
  const { currentEmployeeId } = usePersistedEmployeeId();
  return (
    <WidgetsEmployeeIdProvider employeeId={currentEmployeeId}>
      <ErrorBoundary
        key={errorKey}
        onError={onError}
        onReset={onReset}
        FallbackComponent={WidgetErrorFallback}
      >
        <InteractiveWidgetWrapper {...props}>
          <AllWidgets
            idiom="TOUCH"
            key={errorKey + ""}
            pollFetchKey={props.pollFetchKey}
            mode={props.storecastState === "FOCUS" ? "FOCUS" : "IDLE"}
            widget={props.widget}
            fetchKey={props.fetchKey}
            storecastId={props.storecastId}
            onEvent={props.onEvent}
            isFocused={isFocused}
            margin={props.margin}
            spaceFocusId={
              props.isSpace
                ? (focus.dashboard?.focusedNodeId ?? undefined)
                : undefined
            }
            highlightedItemId={
              (isFocused || props.isSpace
                ? (focus.dashboard?.focusedDetailItemId ?? undefined)
                : undefined) ?? props.selectedIssueId
            }
          />
        </InteractiveWidgetWrapper>
      </ErrorBoundary>
    </WidgetsEmployeeIdProvider>
  );
}
